// Web-exclusive HelpSupport screen
// FAQ accordion with email support and question form

import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { ArrowLeft, HelpCircle, Mail, Send, ChevronDown, ChevronUp } from 'lucide-react';
import WebLayout from '../../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../../constants/webStyles';
import { submitHelp } from '../../../services/shared';
import { useIsMobile } from '../../../hooks/useIsMobile';

const HelpSupportScreen = () => {
  const navigation = useNavigation();
  const { isDoctor } = useSelector((state) => state.user);
  const isMobile = useIsMobile();

  // Form state
  const [helpMessage, setHelpMessage] = useState('');
  const [errorFields, setErrorFields] = useState([]);

  // Toast state
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  // Accordion state
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // FAQ list based on user role
  const FAQList = isDoctor
    ? [
        {
          question: 'Can I update my profile after registration?',
          answer:
            'Yes, you can update your profile anytime under the "Account Settings" or "My Profile" section of the app.',
        },
        {
          question: 'How do I find locum opportunities?',
          answer:
            'Browse available shifts in "Jobs" page by using filters like location, specialty, pay rate, and schedule. You can also enable notifications for new shifts matching your preferences.',
        },
        {
          question: 'Can I apply for multiple shifts at once?',
          answer:
            'Yes, you can apply for multiple shifts. Be sure to check for overlapping schedules to avoid conflicts.',
        },
        {
          question: 'Can I shortlist jobs I am interested in?',
          answer:
            'Yes, you can shortlist and save them to "favorite jobs" by tagging them with "Star" and saving these shifts to review and apply later. Remember that the facility can accept and approve another applicant.',
        },
        {
          question: 'How do I know if my application is successful?',
          answer:
            'You will receive a shortlisted notification once the healthcare facility accepts your application then you need to accept the job for confirmation ASAP before other shortlisted applicants accept the same job.',
        },
        {
          question: 'How am I paid for completed shifts?',
          answer:
            'Payments are paid by the facility either cash payment or bank transfer to your account which you and the facility may need to agree upon after shift completion.',
        },
        {
          question: 'Are there any fees for using the app.',
          answer:
            'The app is free for locum professionals to use. All fees payable by healthcare facilities are displayed transparently.',
        },
        {
          question: 'What happens if there is a payment issue or delayed?',
          answer:
            'Supermedic is not responsible for any payment issues or delays. You may contact support through the app, providing details about the shift and payment issue. We will try to resolve it as soon as possible failing which we may blacklist the facility for future post.',
        },
        {
          question: 'Can I negotiate pay rates?',
          answer: 'No, pay rates are determined by the healthcare facility.',
        },
        {
          question: 'Can I cancel a booked shift?',
          answer:
            "Yes, but cancellations should follow the app's cancellation policy, which may include a minimum notice period.",
        },
        {
          question: 'How do I check shift details?',
          answer:
            'Once you are booked, shift details, including time, contact person and location with Google Maps / Waze direction links will be available in your "Job Application" section.',
        },
        {
          question: "What if I'm late or can't attend a shift?",
          answer:
            'Inform the healthcare facility and app support team immediately to discuss your situation and minimize disruption.',
        },
        {
          question: 'How can I contact support?',
          answer:
            'You can reach out via email in the "Help" section of the app.',
        },
        {
          question: 'How far in advance should I cancel a shift?',
          answer:
            'The notice period varies but is generally 24-48 hours. Last-minute cancellations may result in penalties or account restrictions.',
        },
        {
          question: 'What if I encounter a technical issue?',
          answer:
            'Try restarting the app or updating it to the latest version. If the issue persists, contact the support team for assistance.',
        },
        {
          question: 'How is my personal data protected?',
          answer:
            'The app uses encryption and complies with relevant data protection regulations, such as GDPR or HIPAA, to keep your data secure.',
        },
        {
          question: 'Can I delete my account?',
          answer:
            'Yes, you can request account deletion in the "Accounts – My Profile" section of the app. Ensure all shifts and payments are settled before deleting the account. It will be good to download the latest report before deleting your account because ALL your data may be deleted at the same time which will not be recovered.',
        },
        {
          question: 'How do I get notified about new shifts?',
          answer:
            'Enable push notifications and set your preferences for location, specialty, and pay rate in the "App Permission - Notification Settings."',
        },
        {
          question: 'Can I work in multiple locations?',
          answer:
            'Yes, you can search for shifts in multiple cities or regions by setting your preferences in the filter accordingly.',
        },
        {
          question: "What if I don't want overnight or weekend shifts?",
          answer:
            "Use the app's filters to exclude shifts during specific hours or days when searching for jobs.",
        },
        {
          question: 'What happens if a shift is canceled by the healthcare facility?',
          answer: 'You will be notified immediately.',
        },
        {
          question: 'Can I find a replacement if I cannot attend a shift?',
          answer:
            'No, you can cancel the job if you cannot attend so the facility can accept another applicant because the new applicant will need to "Check-in" at the facility for rating upon completion of the job.',
        },
        {
          question: 'When do I get paid?',
          answer:
            'Payment may take up to 24 hours and in some exceptional cases, 3 days. You may check your bank account periodically. Reachout to us at support@supermedic.health for further enquiries.',
        },
        {
          question: 'I need to cancel my shift',
          answer:
            'You will not receive payments if you have initiated cancellation, this may also affect your ratings.',
        },
        {
          question: 'What does supermedic charge?',
          answer: 'Postings are free to all users.',
        },
        {
          question: 'What happens if I do not check-in?',
          answer:
            'It is the Doctors responsibility to be on time and to request for the QR code from the clinic or facility. Within a check-in in the Supermedic platform, doctors are deemed absent and will not receive payments.',
        },
      ]
    : [
        {
          question: 'Will I be charged to post a shift?',
          answer: 'It is absolutely free to post a shift on Supermedic.',
        },
        {
          question: 'How will the doctor know when to arrive and who to see?',
          answer:
            'Supermedic provides detailed instructions and timely reminders from Application to completion of each shift. You may include specific instructions under notes when posting a shift.',
        },
        {
          question: 'How do I pay the candidate?',
          answer:
            'You are required to make the necessary payment directly to the candidate.',
        },
        {
          question: 'How do I post an urgent shift?',
          answer:
            'Any shift posted on the same day as it is required to be filled is automatically labelled as an urgent shift and will be highlighted on the platform. Urgent shifts are also broadcasted to qualified doctors for said position.',
        },
      ];

  // Toast notification helper
  const showToast = (message, type = 'info') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, 3000);
  };

  // Submit question mutation
  const submitMutation = useMutation({
    mutationFn: submitHelp,
    onSuccess: () => {
      setHelpMessage('');
      setErrorFields([]);
      showToast('Question submitted successfully!', 'success');
    },
    onError: (err) => {
      showToast(
        err?.response?.data?.message || 'Failed to submit question',
        'error'
      );
    },
  });

  // Form validation
  const validateForm = () => {
    const errors = [];
    if (!helpMessage?.trim()) errors.push('helpMessage');
    setErrorFields(errors);
    return errors.length === 0;
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validateForm()) return;

    submitMutation.mutate({
      type: 'question',
      content: helpMessage.trim(),
    });
  };

  // Toggle FAQ accordion
  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  // Handle back navigation
  const handleBack = () => {
    navigation.goBack();
  };

  // Handle email link
  const handleEmailPress = () => {
    Linking.openURL('mailto:support@supermedic.health');
  };

  const isSaving = submitMutation.isPending;

  return (
    <WebLayout>
      {/* Toast Notification */}
      {toast && (
        <View
          style={[
            styles.toast,
            toast.type === 'success' && styles.toastSuccess,
            toast.type === 'error' && styles.toastError,
          ]}
        >
          <Text style={styles.toastText}>{toast.message}</Text>
        </View>
      )}

      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View style={styles.pageHeader}>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <View style={styles.backButtonContent}>
              <ArrowLeft size={16} color={webColors.textSecondary} />
              <Text style={styles.backButtonText}>Back to Settings</Text>
            </View>
          </TouchableOpacity>
          <View style={styles.titleRow}>
            <HelpCircle size={28} color={webColors.primary} />
            <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Help & Support</Text>
          </View>
          <Text style={styles.pageSubtitle}>
            Find answers to common questions or contact our support team.
          </Text>
        </View>

        {/* Content Card */}
        <View style={styles.contentCard}>
          {/* Email Section */}
          <View style={styles.emailSection}>
            <Text style={styles.sectionTitle}>Contact Us</Text>
            <TouchableOpacity style={styles.emailRow} onPress={handleEmailPress}>
              <View style={styles.emailIcon}>
                <Mail size={20} color={webColors.primary} />
              </View>
              <View style={styles.emailInfo}>
                <Text style={styles.emailLabel}>Email Address</Text>
                <Text style={styles.emailLink}>support@supermedic.health</Text>
              </View>
            </TouchableOpacity>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* FAQ Section */}
          <View style={styles.faqSection}>
            <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>
            {FAQList.map((item, index) => (
              <View key={index} style={styles.faqItem}>
                <TouchableOpacity
                  style={styles.faqQuestion}
                  onPress={() => toggleFAQ(index)}
                >
                  <Text style={styles.faqQuestionText}>{item.question}</Text>
                  {expandedFAQ === index ? (
                    <ChevronUp size={20} color={webColors.textMuted} />
                  ) : (
                    <ChevronDown size={20} color={webColors.textMuted} />
                  )}
                </TouchableOpacity>
                {expandedFAQ === index && (
                  <View style={styles.faqAnswer}>
                    <Text style={styles.faqAnswerText}>{item.answer}</Text>
                  </View>
                )}
              </View>
            ))}
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          {/* Question Form */}
          <View style={styles.formSection}>
            <Text style={styles.sectionTitle}>Still Have Questions?</Text>
            <Text style={styles.formDescription}>
              Send us a message and we'll get back to you as soon as possible.
            </Text>
            <TextInput
              style={[
                styles.formInput,
                errorFields.includes('helpMessage') && styles.formInputError,
              ]}
              value={helpMessage}
              onChangeText={(v) => {
                setHelpMessage(v);
                if (errorFields.includes('helpMessage')) {
                  setErrorFields((prev) => prev.filter((f) => f !== 'helpMessage'));
                }
              }}
              placeholder="Type your question here..."
              placeholderTextColor={webColors.textMuted}
              multiline
              textAlignVertical="top"
            />
            <TouchableOpacity
              style={[styles.submitButton, isSaving && styles.buttonDisabled]}
              onPress={handleSubmit}
              disabled={isSaving}
            >
              <Send size={18} color="#ffffff" />
              <Text style={styles.submitButtonText}>
                {isSaving ? 'Sending...' : 'Send Question'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={{ height: 60 }} />
      </ScrollView>
    </WebLayout>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },

  // Toast
  toast: {
    position: 'absolute',
    top: 80,
    right: 20,
    backgroundColor: webColors.cardBackground,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: webBorderRadius.small,
    ...webShadows.modal,
    zIndex: 1000,
    minWidth: 300,
    borderLeftWidth: 4,
    borderLeftColor: webColors.info,
  },
  toastSuccess: {
    borderLeftColor: webColors.success,
  },
  toastError: {
    borderLeftColor: webColors.error,
  },
  toastText: {
    fontSize: 14,
    color: webColors.textPrimary,
  },

  // Header
  pageHeader: {
    marginBottom: 24,
  },
  backButton: {
    marginBottom: 16,
  },
  backButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textSecondary,
  },

  // Content Card
  contentCard: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    padding: webSpacing.cardPadding,
    ...webShadows.card,
  },

  // Section Title
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 16,
  },

  // Email Section
  emailSection: {
    marginBottom: 8,
  },
  emailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  emailIcon: {
    width: 44,
    height: 44,
    borderRadius: webBorderRadius.small,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emailInfo: {
    flex: 1,
  },
  emailLabel: {
    fontSize: 13,
    color: webColors.textMuted,
    marginBottom: 2,
  },
  emailLink: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.primary,
  },

  // Divider
  divider: {
    height: 1,
    backgroundColor: webColors.border,
    marginVertical: 24,
  },

  // FAQ Section
  faqSection: {
    marginBottom: 8,
  },
  faqItem: {
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  faqQuestion: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    gap: 16,
  },
  faqQuestionText: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  faqAnswer: {
    paddingBottom: 16,
    paddingRight: 36,
  },
  faqAnswerText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },

  // Form Section
  formSection: {
    marginTop: 8,
  },
  formDescription: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 16,
  },
  formInput: {
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 15,
    color: webColors.textPrimary,
    backgroundColor: webColors.cardBackground,
    minHeight: 120,
  },
  formInputError: {
    borderColor: webColors.error,
    borderWidth: 2,
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: webColors.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: webBorderRadius.medium,
    alignSelf: 'flex-start',
    marginTop: 16,
    ...webShadows.button,
  },
  submitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});

export default HelpSupportScreen;
